import { useState } from 'react';
import { NavBar } from '../../components/NavBar.tsx';
import { Icon } from '../../components/Icon.tsx';
import { Alert } from '../../components/Overlay.tsx';
import { ErrorState } from '../../components/DataState.tsx';
import { ApiError } from '../../api/client.ts';
import { createSheet, patchSheet } from '../../api/rooms.ts';
import { weekTitle } from '../../domain.ts';
import type { Group, Person, Sheet } from '../../domain.ts';
import { MemberPick } from './GroupForm.tsx';
import { today } from '../book/shared.ts';

/**
 * F3-3 기록지 만들기 · F4-4-1 기록지 수정.
 *
 * **한 화면입니다.** 정하는 것이 같습니다 — 제목 · 모임 날짜 · 참여 구성원.
 *
 * 제목은 날짜에 맞춰 「7월 5째주 순모임」으로 미리 채워지고, **날짜를 바꾸면
 * 따라 바뀝니다.** 다만 손으로 고친 뒤에는 그대로 둡니다 — 직접 적은 제목이
 * 날짜를 건드리다 지워지면 다시 적어야 합니다.
 *
 * 참여 구성원은 **전체가 켜진 채로 열리고 빠진 사람만 끕니다.** 아무것도
 * 손대지 않은 상태가 «다 왔다»인 편이, 매번 전원을 켜는 것보다 실제 모임에
 * 가깝습니다.
 *
 * 수정에서 **이미 적힌 사람을 끄면 한 번 더 묻습니다** (F4-4-1-1). 실제로
 * 지워지는 때는 저장을 누를 때입니다 — 그 전에 ✕ 로 나가면 아무것도
 * 지워지지 않습니다.
 */

interface Props {
  group: Group;
  /** 수정이면 지금 기록지, 만들기면 없음 */
  sheet?: Sheet;
  onCancel: () => void;
  onSaved: (sheet: Sheet) => void;
}

export function SheetForm({ group, sheet, onCancel, onSaved }: Props) {
  const editing = sheet !== undefined;

  const [metOn, setMetOn] = useState(sheet?.metOn ?? today());
  const [title, setTitle] = useState(sheet?.title ?? weekTitle(sheet?.metOn ?? today()));
  /** 제목을 손으로 고쳤는가 — 그 뒤로는 날짜가 제목을 건드리지 않습니다 */
  const [titleOwned, setTitleOwned] = useState(editing);
  const [picked, setPicked] = useState<Set<string>>(
    new Set(
      (sheet?.participants ?? group.members).map((p) => p.userId),
    ),
  );
  /** 적힌 것이 있어 한 번 더 물어야 하는 사람들 */
  const [confirming, setConfirming] = useState<Person[] | null>(null);
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);

  /** 이 사람에게 적힌 것이 있는가 — 기도 노트든 결단이든 한 줄이라도 */
  const hasWritten = (userId: string) =>
    (sheet?.items ?? []).some((item) =>
      item.entries.some((e) => e.member.userId === userId && e.written),
    );

  const all = group.members;
  const everyone = picked.size === all.length;
  const some = picked.size > 0 && !everyone;

  const setDate = (date: string) => {
    setMetOn(date);
    // 직접 고친 제목은 그대로 둡니다
    if (!titleOwned) setTitle(weekTitle(date));
  };

  /** 끄려는 사람 중 적힌 것이 있는 사람들. 있으면 먼저 묻습니다 */
  const ask = (turningOff: string[]) => {
    const risky = turningOff.filter(hasWritten);
    if (risky.length === 0) return false;
    setConfirming(
      all.filter((m) => risky.includes(m.userId)),
    );
    return true;
  };

  const toggle = (userId: string) => {
    if (picked.has(userId)) {
      if (ask([userId])) return;
      setPicked((was) => {
        const next = new Set(was);
        next.delete(userId);
        return next;
      });
    } else {
      setPicked((was) => new Set(was).add(userId));
    }
  };

  /** 일부만 켜져 있으면 전부 켜고, 전부 켜져 있으면 전부 끕니다 */
  const toggleAll = () => {
    if (everyone) {
      if (ask([...picked])) return;
      setPicked(new Set());
    } else {
      setPicked(new Set(all.map((m) => m.userId)));
    }
  };

  const save = async () => {
    const participants = [...picked];
    if (participants.length === 0) {
      setFailure('참여한 구성원을 한 명 이상 골라 주세요.');
      return;
    }
    setBusy(true);
    setFailure(null);
    try {
      onSaved(
        editing
          ? await patchSheet(sheet.sheetId, { title: title.trim(), metOn, participants })
          : await createSheet(group.groupId, { title: title.trim(), metOn, participants }),
      );
    } catch (e) {
      setFailure(e instanceof ApiError ? e.message : '저장하지 못했습니다.');
      setBusy(false);
    }
  };

  return (
    <div className="screen">
      <NavBar
        title={editing ? '기록지 수정' : '기록지 만들기'}
        subtitle={group.name}
        start={{ icon: 'close', label: '나가기', onClick: onCancel }}
        end={{ icon: 'check', label: '저장', accent: true, onClick: save }}
      />

      <div className="screen__body">
        {failure && <ErrorState message={failure} onRetry={() => setFailure(null)} />}

        <label className="field">
          <span className="field__label">제목</span>
          <input
            className="input"
            value={title}
            onChange={(e) => {
              setTitle(e.target.value);
              setTitleOwned(true);
            }}
          />
        </label>

        <label className="field">
          <span className="field__label">모임 날짜</span>
          <input
            className="input"
            type="date"
            value={metOn}
            onChange={(e) => setDate(e.target.value)}
          />
        </label>

        <div className="field">
          <div className="listhead__row">
            <span className="field__label">
              구성원 선택 · <b>{picked.size} / {all.length}</b>
            </span>
            {/* 일부만 켜져 있으면 가운데 줄(−)로 보입니다 */}
            <button type="button" className="pickall" onClick={toggleAll}>
              전체 선택
              <span className={everyone ? 'chk chk--on' : some ? 'chk chk--some' : 'chk'}>
                {everyone && <Icon name="check" size={12} />}
              </span>
            </button>
          </div>

          <div className="stack stack--tight">
            {all.map((m) => (
              <MemberPick
                key={m.userId}
                person={m}
                on={picked.has(m.userId)}
                tag={
                  m.userId === group.ownerId
                    ? '그룹장'
                    : undefined
                }
                onToggle={() => toggle(m.userId)}
              />
            ))}
          </div>
        </div>

        <div className="card card--flat">
          <p className="hint">
            선택한 구성원마다 <b>기도 노트</b>와 <b>결단</b>을 적을 칸이 생깁니다.
          </p>
        </div>

        <div className="btn-row btn-row--foot">
          <button
            type="button"
            className="btn btn--primary"
            disabled={busy || title.trim() === '' || picked.size === 0}
            onClick={save}
          >
            {busy ? '저장 중…' : editing ? '저장' : '완료'}
          </button>
        </div>
      </div>

      {/* F4-4-1-1 — 적힌 것이 있는 사람을 뺄 때 */}
      {confirming && (
        <Alert
          title={
            confirming.length === 1
              ? `${confirming[0].name ?? '이 구성원'}을 뺄까요?`
              : `${confirming.length}명을 뺄까요?`
          }
          onClose={() => setConfirming(null)}
          actions={
            <>
              <button
                type="button"
                className="btn btn--secondary"
                onClick={() => setConfirming(null)}
              >
                취소
              </button>
              <button
                type="button"
                className="btn btn--danger"
                onClick={() => {
                  const gone = new Set(confirming.map((m) => m.userId));
                  setPicked((was) => new Set([...was].filter((id) => !gone.has(id))));
                  setConfirming(null);
                }}
              >
                빼기
              </button>
            </>
          }
        >
          <p>
            {confirming.length > 1 && (
              <>
                <b>{confirming.map((m) => m.name ?? '이름 없음').join(' · ')}</b>
                <br />
              </>
            )}
            이 기록지의 <b>기도 노트 · 결단 줄이 지워집니다.</b>
          </p>
          <p className="alert__note">
            Prayer Book 에 저장한 기도 노트는 남습니다. 실제로 지워지는 것은 <b>저장을 누를 때</b>
            이고, 그 전에 ✕ 로 나가면 아무것도 지워지지 않습니다.
          </p>
        </Alert>
      )}
    </div>
  );
}
